/**
 * Traces physiques des visiteurs (§16.1, livré en 0.30.0) — **logique pure**.
 *
 * Le compteur de visites, réparé en 0.20.0, devient VISIBLE sur la ville : chaque
 * visiteur laisse une fleur, et les paliers laissent du mobilier. Depuis la 0.36.0
 * (demande de Benoit : « possibilité de déplacer et de vendre — motivation de
 * partage »), les traces sont des ENTITÉS d'état : elles apparaissent une fois,
 * se vendent contre des pièces, se déplacent — et ne repoussent jamais toutes
 * seules, les compteurs d'apparition étant monotones (règle 21).
 */

import { ECONOMY, GEM_SPENDING, VISITOR_DECORATIONS } from '@/config/balance';
import { decorationBySprite } from '@/config/decorations';
import type { BackpackItem, CitySnapshot, IslandState, VisitorTrace } from './gameState';

/**
 * Plafond de fleurs affichées.
 *
 * « Une fleur par visiteur » est la promesse du début ; une ville à 500 visiteurs
 * couverte de 500 jardinières serait une pépinière, pas une ville — et le §16.1
 * matérialise ces volumes-là par les PALIERS, pas par l'accumulation. Deux douzaines
 * suffisent à dire « du monde passe ici » sans manger le terrain constructible.
 */
export const FLOWERBED_CAP = 24;

export interface VisitorTraceCounts {
  /** Jardinières fleuries à semer, bornées par `FLOWERBED_CAP`. */
  readonly flowerbeds: number;
  /** Trophées de palier atteints, dans l'ordre des paliers. */
  readonly landmarks: readonly string[];
}

/** Quantités de traces pour un nombre de visiteurs uniques donné. */
export function traceCounts(visitorsReceived: number): VisitorTraceCounts {
  // `NaN` traverse `Math.max` et `Math.floor` sans bruit : un compteur corrompu doit
  // donner zéro trace, pas une ville pleine de NaN.
  const visitors = Number.isFinite(visitorsReceived)
    ? Math.max(0, Math.floor(visitorsReceived))
    : 0;

  return {
    flowerbeds: Math.min(FLOWERBED_CAP, visitors * VISITOR_DECORATIONS.flowerPerVisitor),
    landmarks: VISITOR_DECORATIONS.tiers
      .filter((tier) => visitors >= tier.at)
      .map((tier) => tier.decoration),
  };
}

/* ── Entités ─────────────────────────────────────────────────────────────── */

/**
 * Prix de vente d'une trace, en SECONDES de production courante.
 *
 * Un prix fixe deviendrait de la poussière dès la deuxième heure — l'économie est
 * exponentielle. Exprimé en temps de production, il reste désirable pour toujours :
 * c'est exactement la « motivation de partage » demandée. Le §14 tient : ces pièces
 * viennent de visites réelles validées par le serveur, pas d'un paiement.
 */
export function traceSellValue(kind: string, production: number): number {
  const seconds = VISITOR_DECORATIONS.sellSeconds[kind] ?? 0;
  // Plancher : même une ville qui produit 0,2/s vend sa fleur pour quelques pièces.
  return Math.max(5, Math.round(seconds * Math.max(0.1, production)));
}

/**
 * Prix de revente d'une décoration ACHETÉE, en pièces (ROADMAP 2.9d).
 *
 * ⚠️ **Indexé sur le prix d'ACHAT en gemmes, jamais sur la production.** C'est toute la
 * différence avec `traceSellValue` juste au-dessus, et c'est ce qui referme la brèche
 * qui avait fait interdire la vente : un taux fixe ne peut pas devenir rentable en fin
 * d'ère, donc l'aller-retour gemmes → décor → pièces n'est jamais une pompe.
 *
 * Rend 0 pour une sorte hors catalogue : sans prix d'achat connu, il n'y a pas de
 * prorata possible, et inventer un montant serait exactement la supposition que la
 * règle 14 interdit. L'appelant refuse alors la vente plutôt que de payer au hasard.
 */
export function boughtDecorationSellValue(kind: string): number {
  const definition = decorationBySprite(kind);
  if (definition === undefined) return 0;
  return Math.round(
    definition.gemCost * GEM_SPENDING.decorationResale.coinsPerGem * ECONOMY.sellRefund,
  );
}

/** Les deux sortes de fleurs alternent : la variété visuelle demandée par Benoit. */
const FLOWER_KINDS = ['flowerbed', 'planter'] as const;

/** Cases occupées par tout ce qui existe sur l'île — bâtiments (emprises), routes, traces. */
function occupiedCells(
  island: IslandState,
  footprintCells: (
    type: string,
    col: number,
    row: number,
  ) => readonly { col: number; row: number }[],
): Set<string> {
  const busy = new Set<string>(island.roads);
  for (const building of island.buildings) {
    for (const cell of footprintCells(building.type, building.col, building.row)) {
      busy.add(`${String(cell.col)}:${String(cell.row)}`);
    }
  }
  for (const trace of island.traces) busy.add(`${String(trace.col)}:${String(trace.row)}`);
  for (const monument of island.monuments) {
    busy.add(`${String(monument.col)}:${String(monument.row)}`);
  }
  return busy;
}

/**
 * Case libre déterministe pour la n-ième trace : le même bruit que le décor
 * (`sin` sur les coordonnées), décalé par l'indice — deux villes identiques font
 * pousser leurs fleurs aux mêmes endroits, et une trace ne saute jamais de case.
 */
function freeCellFor(
  island: IslandState,
  busy: ReadonlySet<string>,
  index: number,
): { col: number; row: number } | null {
  const { cols, rows } = island.grid;
  const total = cols * rows;
  const start = Math.abs(Math.floor(Math.sin(index * 12.9898) * 43758.5453)) % total;

  for (let step = 0; step < total; step++) {
    const at = (start + step * 7919) % total; // 7919 premier : parcourt toute la grille.
    const col = at % cols;
    const row = Math.floor(at / cols);
    if (!busy.has(`${String(col)}:${String(row)}`)) return { col, row };
  }
  return null;
}

export interface SpawnResult {
  readonly snapshot: CitySnapshot;
  /** Les traces NÉES par cet appel — c'est elles que la fanfare célèbre. */
  readonly born: readonly VisitorTrace[];
}

/**
 * Fait apparaître les traces méritées et pas encore nées. Idempotent : rappeler
 * sans nouveau visiteur ne change rien. Les compteurs d'apparition ne reculent
 * jamais — vendre une trace ne la fait pas repousser (règle 21).
 */
export function spawnVisitorTraces(
  snapshot: CitySnapshot,
  footprintCells: (
    type: string,
    col: number,
    row: number,
  ) => readonly { col: number; row: number }[],
  now: number,
): SpawnResult {
  const island = snapshot.islands[0];
  if (island === undefined) return { snapshot, born: [] };

  const target = traceCounts(snapshot.visitorsReceived);
  const born: VisitorTrace[] = [];
  const busy = occupiedCells(island, footprintCells);

  let flowersSpawned = snapshot.flowersSpawned;
  while (flowersSpawned < target.flowerbeds) {
    const cell = freeCellFor(island, busy, flowersSpawned);
    if (cell === null) break; // Grille pleine : les fleurs attendront un terrain.
    const kind = FLOWER_KINDS[flowersSpawned % FLOWER_KINDS.length] ?? 'flowerbed';
    born.push({ id: `vt-${String(now)}-${String(flowersSpawned)}`, kind, ...cell, placedAt: now });
    busy.add(`${String(cell.col)}:${String(cell.row)}`);
    flowersSpawned++;
  }

  const landmarksSpawned = [...snapshot.landmarksSpawned];
  for (const landmark of target.landmarks) {
    if (landmarksSpawned.includes(landmark)) continue;
    const cell = freeCellFor(island, busy, 1000 + landmarksSpawned.length);
    if (cell === null) break;
    born.push({ id: `vt-${String(now)}-${landmark}`, kind: landmark, ...cell, placedAt: now });
    busy.add(`${String(cell.col)}:${String(cell.row)}`);
    landmarksSpawned.push(landmark);
  }

  if (born.length === 0) return { snapshot, born };

  return {
    snapshot: {
      ...snapshot,
      flowersSpawned,
      landmarksSpawned,
      islands: snapshot.islands.map((entry, index) =>
        index === 0 ? { ...entry, traces: [...entry.traces, ...born] } : entry,
      ),
      updatedAt: now,
    },
    born,
  };
}

export type TraceActionResult =
  | { readonly ok: true; readonly snapshot: CitySnapshot; readonly coins?: number }
  | { readonly ok: false; readonly refusal: 'not-found' | 'cell-occupied' | 'bought' };

/** Vend une trace : elle disparaît, les pièces arrivent. Elle ne repoussera pas. */
export function sellVisitorTrace(
  snapshot: CitySnapshot,
  id: string,
  production: number,
  now: number,
): TraceActionResult {
  const island = snapshot.islands[0];
  const trace = island?.traces.find((entry) => entry.id === id);
  if (island === undefined || trace === undefined) return { ok: false, refusal: 'not-found' };

  /*
    ── DEUX BARÈMES, PARCE QU'IL Y A DEUX ORIGINES (ROADMAP 2.4 puis 2.9d) ──────

    Une trace de VISITEUR se vend en secondes de production courante : elle récompense
    des visites réelles validées par le serveur, et un prix fixe deviendrait de la
    poussière dès la deuxième heure.

    Une décoration ACHETÉE ne peut pas suivre ce barème — ce serait une conversion
    gemmes → pièces dont le taux monte avec la partie, donc une pompe. C'est ce qui
    avait fait INTERDIRE sa vente en 0.62.0. Le refus est levé, mais pas la règle : son
    prix est indexé sur ce qu'elle a coûté en gemmes, donc fixe.

    Le refus `'bought'` survit pour un seul cas — une décoration payée dont la sorte
    n'est dans aucun catalogue. Sans prix d'achat connu, il n'y a pas de prorata, et
    payer au hasard vaut moins que dire non.
  */
  const bought = trace.bought === true;
  const coins = bought
    ? boughtDecorationSellValue(trace.kind)
    : traceSellValue(trace.kind, production);
  if (bought && coins <= 0) return { ok: false, refusal: 'bought' };
  return {
    ok: true,
    coins,
    snapshot: {
      ...snapshot,
      coins: snapshot.coins + coins,
      islands: snapshot.islands.map((entry, index) =>
        index === 0
          ? { ...entry, traces: entry.traces.filter((candidate) => candidate.id !== id) }
          : entry,
      ),
      updatedAt: now,
    },
  };
}

/** Déplace une trace vers une case libre. */
export function moveVisitorTrace(
  snapshot: CitySnapshot,
  id: string,
  col: number,
  row: number,
  footprintCells: (
    type: string,
    col: number,
    row: number,
  ) => readonly { col: number; row: number }[],
  now: number,
): TraceActionResult {
  const island = snapshot.islands[0];
  const trace = island?.traces.find((entry) => entry.id === id);
  if (island === undefined || trace === undefined) return { ok: false, refusal: 'not-found' };

  const { cols, rows } = island.grid;
  if (col < 0 || row < 0 || col >= cols || row >= rows) {
    return { ok: false, refusal: 'cell-occupied' };
  }
  const busy = occupiedCells(island, footprintCells);
  busy.delete(`${String(trace.col)}:${String(trace.row)}`);
  if (busy.has(`${String(col)}:${String(row)}`)) return { ok: false, refusal: 'cell-occupied' };

  return {
    ok: true,
    snapshot: {
      ...snapshot,
      islands: snapshot.islands.map((entry, index) =>
        index === 0
          ? {
              ...entry,
              traces: entry.traces.map((candidate) =>
                candidate.id === id ? { ...candidate, col, row } : candidate,
              ),
            }
          : entry,
      ),
      updatedAt: now,
    },
  };
}

/* ── Réparation : rien de posé ne reste ENTERRÉ ──────────────────────────── */

export interface BuriedRepair {
  readonly snapshot: CitySnapshot;
  /** Traces déplacées vers une case libre. */
  readonly moved: number;
  /** Décorations PAYÉES rangées au sac faute de case libre — rien n'est perdu. */
  readonly stashed: number;
  /** Traces de visiteur perdues : la grille était pleine. Elles ne repousseront pas. */
  readonly dropped: number;
}

/** Cases prises par tout SAUF les traces — c'est ce qui les enterre. */
function blockedCells(
  island: IslandState,
  footprintCells: (
    type: string,
    col: number,
    row: number,
  ) => readonly { col: number; row: number }[],
): Set<string> {
  const busy = new Set<string>(island.roads);
  for (const building of island.buildings) {
    for (const cell of footprintCells(building.type, building.col, building.row)) {
      busy.add(`${String(cell.col)}:${String(cell.row)}`);
    }
  }
  for (const monument of island.monuments) {
    busy.add(`${String(monument.col)}:${String(monument.row)}`);
  }
  return busy;
}

/**
 * Sort de terre les traces qu'un bâtiment ou une rue a recouvertes.
 *
 * ══ Pourquoi cette fonction existe (rapport de Benoit, 20 août) ══════════════
 *
 * `canPlace` interdit désormais de bâtir sur une décoration — mais deux choses
 * restaient : les villes **déjà** dans cet état, et les RUES, dont le tracé est
 * calculé et peut légitimement traverser une case décorée. Une trace recouverte est
 * injoignable : le tap résout le bâtiment d'abord, donc plus moyen de la vendre ni de
 * la déplacer, et une décoration payée finissait à la poubelle au changement d'ère.
 *
 * L'invariant est celui du sac à dos (ROADMAP 2.9c), étendu d'un cran : **tout ce qui
 * est posé est atteignable**. On répare plutôt que d'interdire, parce qu'un refus de
 * pose dû à une fleur située quatre cases plus loin serait incompréhensible.
 *
 * Ordre des recours, du moins destructeur au plus : déplacer sur une case libre ;
 * sinon ranger au sac si c'est payé (2.9c — on ne fait pas disparaître ce qui a été
 * acheté) ; sinon, et pour les seules traces de visiteur, abandonner — c'est déjà ce
 * que fait `deserialize` d'une trace hors grille.
 *
 * Idempotente : sur une ville saine, elle rend le MÊME objet snapshot, donc n'écrit
 * aucune sauvegarde et ne déclenche aucune synchro.
 */
export function relocateBuriedTraces(
  snapshot: CitySnapshot,
  footprintCells: (
    type: string,
    col: number,
    row: number,
  ) => readonly { col: number; row: number }[],
  now: number,
): BuriedRepair {
  const island = snapshot.islands[0];
  if (island === undefined) return { snapshot, moved: 0, stashed: 0, dropped: 0 };

  const blocked = blockedCells(island, footprintCells);
  const buried = island.traces.filter((trace) =>
    blocked.has(`${String(trace.col)}:${String(trace.row)}`),
  );
  if (buried.length === 0) return { snapshot, moved: 0, stashed: 0, dropped: 0 };

  // Les traces saines gardent leur case : elles font partie de ce qui est occupé.
  const busy = new Set(blocked);
  for (const trace of island.traces) {
    if (buried.includes(trace)) continue;
    busy.add(`${String(trace.col)}:${String(trace.row)}`);
  }

  const kept: VisitorTrace[] = island.traces.filter((trace) => !buried.includes(trace));
  const stash: BackpackItem[] = [];
  let moved = 0;
  let dropped = 0;

  for (const [index, trace] of buried.entries()) {
    const cell = freeCellFor(island, busy, index);
    if (cell !== null) {
      busy.add(`${String(cell.col)}:${String(cell.row)}`);
      kept.push({ ...trace, col: cell.col, row: cell.row });
      moved++;
      continue;
    }

    const definition = trace.bought === true ? decorationBySprite(trace.kind) : undefined;
    if (definition !== undefined) {
      stash.push({ id: trace.id, decorationId: definition.id });
      continue;
    }
    dropped++;
  }

  return {
    snapshot: {
      ...snapshot,
      backpack: [...snapshot.backpack, ...stash],
      islands: snapshot.islands.map((entry, index) =>
        index === 0 ? { ...entry, traces: kept } : entry,
      ),
      updatedAt: now,
    },
    moved,
    stashed: stash.length,
    dropped,
  };
}
